import isEqualWith from 'lodash.isequalwith';

import { isNotNullish } from '@app/helpers/predicate';
import type {
  Magento2_CountryCodeEnum,
  Magento2_CustomerAddressInput,
} from '@__generated__/globalTypes';

import type { CartBillingAddressDetails } from './__generated__/CartBillingAddressDetails';
import type { CartShippingAddressDetails } from './__generated__/CartShippingAddressDetails';
import type { AddressCountriesQuery_magento2_countries } from './AddressForm/__generated__/AddressCountriesQuery';
import type { CustomerAddressDetails } from './CustomerAddresses/__generated__/CustomerAddressDetails';
import type { AddressFormValues, AddressType } from './Address.types';

export type CartAddressDetails =
  | CartBillingAddressDetails
  | CartShippingAddressDetails;

/*
 *
 * Map Magento2 addresses to an unique address type
 * to work around region/country inconsistencies.
 *
 */
export const mapToAddressType = (
  address?: CustomerAddressDetails | CartAddressDetails | null,
): AddressType | null => {
  if (!address) return null;

  if (isCartAddress(address)) {
    const { country, region, ...addressWithNoDuplicates } = address;

    return {
      ...addressWithNoDuplicates,
      country_code: country.code as Magento2_CountryCodeEnum,
      region_id: region?.regionId,
    };
  }

  if (isCustomerAddress(address)) {
    const { countryCode, vatId, region, ...addressWithNoDuplicates } = address;

    return {
      ...addressWithNoDuplicates,
      country_code: countryCode,
      region_id: region?.regionId,
      vat_id: vatId,
    };
  }

  return null;
};

/**
 *
 * Adjust form values to fit customer address input data. New addresses
 * need to be submitted without id and __typename, and with correct region attribute
 *
 */
export const mapToNewCustomerAddress = (
  formValues: AddressFormValues,
): Magento2_CustomerAddressInput => {
  const {
    id: _id,
    __typename,
    region_id,
    street,
    ...customerAddress
  } = formValues;

  return {
    // Optional phone still needs to be sent
    telephone: '',

    ...customerAddress,

    // Clear optional fields
    street: street.filter(Boolean),
    ...(region_id && { region: { region_id } }),
  };
};

/*
 *
 * Since Magento 2 provides no ID for cart addresses,
 * manual comparison is required to trace back a cart address to its identifiable
 * customer address.
 *
 */
export const isAddressEqual = (
  { __typename, id: _id, vat_id: _vat_id, ...address }: AddressType,
  {
    __typename: __typename2,
    vat_id: _vat_id2,
    id: _id2,
    ...address2
  }: AddressType,
): boolean =>
  isEqualWith(address, address2, (a: unknown, b: unknown) => {
    /*
     * Below we regard all falsy values as identical, e.g.:
     * { street: ['Boddinstr. 14', null] } equal to { street: ['Boddinstr. 14'] }
     * { company: null } equal to { company: '' }
     * and so on.
     */

    if (Array.isArray(a) && Array.isArray(b)) {
      const truthyValuesA = a.filter(isNotNullish);
      const truthyValuesB = b.filter(isNotNullish);

      return (
        truthyValuesA.length === truthyValuesB.length &&
        truthyValuesA.every((el, index) => el === truthyValuesB[index])
      );
    }

    return !a && !b ? true : undefined;
  });

const isCartAddress = (address: unknown): address is CartAddressDetails =>
  ['Magento2_ShippingCartAddress', 'Magento2_BillingCartAddress'].includes(
    (address as CartAddressDetails).__typename ?? '',
  );

const isCustomerAddress = (
  address: unknown,
): address is CustomerAddressDetails =>
  (address as CustomerAddressDetails).__typename === 'Magento2_CustomerAddress';

export const sortCountries = (
  countries: AddressCountriesQuery_magento2_countries[] | null,
): AddressCountriesQuery_magento2_countries[] | null =>
  countries &&
  countries.sort((a, b): number => {
    if (!a.fullNameLocale || !b.fullNameLocale) return 0;
    return a?.fullNameLocale.localeCompare(b?.fullNameLocale);
  });
